import { List, OrderedMap } from 'immutable';
import { useState } from 'react';
import type { UserResponse } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';

import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import FontIcon from '@/shared/ui/FontIcon';
import { useWiwDispatch, useWiwSelector } from '@/store';
import { WORKCHAT_VIEWS } from '@/workchat/constants';
import type { HeaderButton } from '@/workchat/v2/components/HeaderV2';
import { VIEW_CONVERSATION } from '@/workchat/v2/components/ViewConversationV2';
import { useStreamProvider } from '@/workchat/v2/providers/StreamProvider';
import { setConversation, viewStackPop, viewStackPush, viewStackReset } from '@/workchat/v2/store/reducer';
import HeaderV3 from '@/workchat/v3/components/HeaderV3/HeaderV3';
import { UserPickerV3 } from '@/workchat/v3/components/UserPickerV3/UserPickerV3';

const NEW_MESSAGE_PARTICIPANT_LIMIT = 9;

export default function NewMessage() {
  const dispatch = useWiwDispatch();
  const account = useWiwSelector(getAuthAccount);
  const user = useWiwSelector(getAuthUser);

  const { client } = useStreamProvider();
  const { setActiveChannel } = useChatContext();

  const [selectedUsers, setSelectedUsers] = useState(List<string>());
  const [userCache, setUserCache] = useState<OrderedMap<string, UserResponse>>(OrderedMap());

  const startConversation = async () => {
    try {
      const channel = client?.channel('direct_message', {
        members: selectedUsers
          .toArray()
          .map(id => `${id}`)
          .concat(`${user.id}`),
        team: `${account.id}`,
      });
      await channel?.create();

      if (channel?.id) {
        setActiveChannel(channel);
        dispatch(viewStackReset());
        dispatch(setConversation(channel.id));
        dispatch(viewStackPush(VIEW_CONVERSATION));
      } else {
        throw new Error('No channel');
      }
    } catch (error) {
      // TODO: Handle Error
      console.log('💀 error', error);
    }
  };

  const back = () => {
    dispatch(viewStackPop());
  };

  const disabled = !selectedUsers.size;

  const buttons: HeaderButton[] = [
    {
      title: 'Back',
      action: back,
      label: [<FontIcon className="mr-1" key="back" icon="chevron-left" />, 'Back'],
      side: 'left',
      color: 'secondary',
    },
    { action: startConversation, label: 'Next', disabled, side: 'right', color: 'primary' },
  ];

  const handleCreateChannel = () => {
    dispatch(viewStackPop());
    dispatch(viewStackPush([WORKCHAT_VIEWS.VIEW_NEW_CHANNEL, { selectedUsers, userCache }]));
  };

  return (
    <div className="NewMessage">
      <HeaderV3 buttons={buttons}>Direct Message</HeaderV3>
      <UserPickerV3
        user={user}
        account={account}
        onUserAdded={selectedUsers => setSelectedUsers(selectedUsers)}
        limit={NEW_MESSAGE_PARTICIPANT_LIMIT}
        limitText={`Direct messages can have up to ${NEW_MESSAGE_PARTICIPANT_LIMIT} members. If you want to include more, create a channel.`}
        limitCTAButtonText="Create a channel"
        onLimitCTAButtonClick={handleCreateChannel}
        onUserCacheChange={userCache => setUserCache(userCache)}
      />
    </div>
  );
}
